import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { congregationApi } from "../../../../../api/cong";
import QueryKeys from "../../../../../api/queryKeys";
import { HGBugsnagNotify } from "../../../../../helpers/bugsnag";
import { MaxSongNumber } from "../../../../../helpers/util";
import { CongSetting, CongSettings } from "../../../../../types/scheduling/settings";
import { DDMnumbers, TextInput } from "../../../settings/settings";

// On a CO visit week the talk title/song come from congregation settings, not
// the schedule itself (#2107). This modal edits just those settings in context.
export function COVisitWeekModal(props: {
  show: boolean;
  setShow: (show: boolean) => void;
  settings: CongSettings;
  langGroupId: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState(props.settings);
  const [saving, setSaving] = useState(false);

  // refresh the local copy whenever the modal opens with newer settings
  useEffect(() => {
    if (props.show) setSettings(props.settings);
  }, [props.show, props.settings]);

  const handleClose = () => props.setShow(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await congregationApi.setSettings(settings, props.langGroupId);
      queryClient.setQueryData([QueryKeys.CongregationSettings, props.langGroupId], resp);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.WMCSchedules] });
      handleClose();
    } catch (err) {
      HGBugsnagNotify("COVisitWeekModal.handleSave", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("general.circuit-overseer")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <TextInput settingKey={CongSetting.CircuitOverseerName} settings={settings} setSettings={setSettings} />
          <TextInput
            settingKey={CongSetting.CircuitOverseerWMTalkTitle}
            settings={settings}
            setSettings={setSettings}
          />
          <TextInput
            settingKey={CongSetting.CircuitOverseerWMServiceTalkTitle}
            settings={settings}
            setSettings={setSettings}
          />
          <DDMnumbers
            settings={settings}
            setSettings={setSettings}
            settingKey={CongSetting.CircuitOverseerWMClosingSong}
            options={MaxSongNumber}
            allowZero
            zeroIsBlank
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          {t("general.cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving} data-testid="co-visit-save">
          {saving ? <Spinner size="sm" animation="border" /> : t("general.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
